import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [stats, setStats] = useState(null);
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState(isAdmin ? (user?.firmId || 'ALL') : (user?.firmId || '1'));
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('RECEIVABLE'); // RECEIVABLE or PAYABLE
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchFirms = async () => {
      try {
        const configRes = await axiosClient.get('/auth/config');
        if (configRes.success) {
          setFirms(configRes.data?.firms || []);
        }
      } catch (e) {
        console.error(e);
      }
    };
    if (isAdmin) fetchFirms();
  }, [isAdmin]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const firmParam = isAdmin ? selectedFirm : (user?.firmId || '');
      const res = await axiosClient.get(`/reports/dashboard?firmId=${firmParam}`);
      if (res.success) {
        setStats(res.data);
      }
    } catch (e) {
      console.error('Error fetching dashboard stats:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFirm]);

  if (loading && !stats) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  const rawReceivables = stats?.receivablesList || [];
  const rawPayables = stats?.payablesList || [];

  const filterList = (list) => {
    if (!searchTerm.trim()) return list;
    const term = searchTerm.toLowerCase().trim();
    return list.filter(
      (item) =>
        (item.AccountName || '').toLowerCase().includes(term) ||
        (item.MobileNo || '').toLowerCase().includes(term)
    );
  };

  const receivablesList = filterList(rawReceivables);
  const payablesList = filterList(rawPayables);

  return (
    <div className="container-fluid p-0">
      {/* Header Banner */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2 pb-2 border-bottom">
        <div>
          <h2 className="fw-bold mb-1 text-dark">
            Welcome, <span className="text-primary">{user?.username}</span>
            {isAdmin && <span className="badge bg-danger ms-2 fs-6">Admin</span>}
          </h2>
          <p className="text-muted mb-0 small">
            Branch: <strong className="text-dark">{user?.firmName}</strong> | Financial Year: <span className="badge bg-secondary">FY {user?.fYear}</span>
          </p>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          {isAdmin && (
            <div className="d-flex align-items-center gap-1 bg-white border p-1 rounded shadow-sm">
              <i className="bi bi-building text-warning ms-1"></i>
              <select
                className="form-select form-select-sm border-0 bg-transparent fw-semibold"
                style={{ width: '220px' }}
                value={selectedFirm}
                onChange={(e) => setSelectedFirm(e.target.value)}
                title="Filter Dashboard by Firm Branch"
              >
                <option value="ALL">★ All Firms (Consolidated)</option>
                {firms.map((f) => (
                  <option key={f.Id} value={f.Id}>
                    {f.firmName}
                  </option>
                ))}
              </select>
            </div>
          )}

          <Link to="/sales/new" className="btn btn-ocean d-flex align-items-center gap-1 shadow-sm">
            <i className="bi bi-cart-plus-fill"></i> New Sale
          </Link>
          <Link to="/purchases/new" className="btn btn-primary d-flex align-items-center gap-1 shadow-sm">
            <i className="bi bi-bag-plus-fill"></i> New Purchase
          </Link>
          <Link to="/payments/in" className="btn btn-teal d-flex align-items-center gap-1 shadow-sm">
            <i className="bi bi-cash-stack"></i> Payment In / Out
          </Link>
        </div>
      </div>

      {/* 6 Pro Metrics Cards Grid */}
      <div className="row g-3 mb-4">
        {/* Today Sale */}
        <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
          <div className="card stat-card shadow-sm border-0 border-start border-4 border-primary h-100 p-3 bg-white">
            <div className="d-flex align-items-center gap-3">
              <div className="stat-icon bg-primary bg-opacity-10 text-primary rounded-circle p-2 fs-4 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                <i className="bi bi-currency-rupee"></i>
              </div>
              <div>
                <div className="text-muted extra-small fw-bold text-uppercase">Today Sale</div>
                <h5 className="fw-bold mb-0 text-dark">₹{parseFloat(stats?.todaySales?.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h5>
                <small className="text-muted">{stats?.todaySales?.count || 0} bills today</small>
              </div>
            </div>
          </div>
        </div>

        {/* Today Purchase */}
        <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
          <div className="card stat-card shadow-sm border-0 border-start border-4 border-info h-100 p-3 bg-white">
            <div className="d-flex align-items-center gap-3">
              <div className="stat-icon bg-info bg-opacity-10 text-info rounded-circle p-2 fs-4 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                <i className="bi bi-bag-check-fill"></i>
              </div>
              <div>
                <div className="text-muted extra-small fw-bold text-uppercase">Today Purchase</div>
                <h5 className="fw-bold mb-0 text-dark">₹{parseFloat(stats?.todayPurchases?.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h5>
                <small className="text-muted">{stats?.todayPurchases?.count || 0} vouchers</small>
              </div>
            </div>
          </div>
        </div>

        {/* Total Sale FY */}
        <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
          <div className="card stat-card shadow-sm border-0 border-start border-4 border-success h-100 p-3 bg-white">
            <div className="d-flex align-items-center gap-3">
              <div className="stat-icon bg-success bg-opacity-10 text-success rounded-circle p-2 fs-4 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                <i className="bi bi-graph-up-arrow"></i>
              </div>
              <div>
                <div className="text-muted extra-small fw-bold text-uppercase">Total Sale (FY)</div>
                <h5 className="fw-bold mb-0 text-dark">₹{parseFloat(stats?.totalSales?.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h5>
                <small className="text-muted">{stats?.totalSales?.count || 0} invoices</small>
              </div>
            </div>
          </div>
        </div>

        {/* Total Purchase FY */}
        <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
          <div className="card stat-card shadow-sm border-0 border-start border-4 border-secondary h-100 p-3 bg-white">
            <div className="d-flex align-items-center gap-3">
              <div className="stat-icon bg-secondary bg-opacity-10 text-secondary rounded-circle p-2 fs-4 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                <i className="bi bi-cart-check-fill"></i>
              </div>
              <div>
                <div className="text-muted extra-small fw-bold text-uppercase">Total Purchase (FY)</div>
                <h5 className="fw-bold mb-0 text-dark">₹{parseFloat(stats?.totalPurchases?.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h5>
                <small className="text-muted">{stats?.totalPurchases?.count || 0} bills</small>
              </div>
            </div>
          </div>
        </div>

        {/* Total Receivable */}
        <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
          <div className="card stat-card shadow-sm border-0 border-start border-4 border-warning h-100 p-3 bg-white">
            <div className="d-flex align-items-center gap-3">
              <div className="stat-icon bg-warning bg-opacity-10 text-warning rounded-circle p-2 fs-4 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                <i className="bi bi-arrow-down-left-circle-fill"></i>
              </div>
              <div>
                <div className="text-muted extra-small fw-bold text-uppercase">Total Receivable</div>
                <h5 className="fw-bold mb-0 text-danger">₹{parseFloat(stats?.totalReceivable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h5>
                <small className="text-muted">From Customers</small>
              </div>
            </div>
          </div>
        </div>

        {/* Total Payable Outstanding */}
        <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
          <div className="card stat-card shadow-sm border-0 border-start border-4 border-danger h-100 p-3 bg-white">
            <div className="d-flex align-items-center gap-3">
              <div className="stat-icon bg-danger bg-opacity-10 text-danger rounded-circle p-2 fs-4 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                <i className="bi bi-arrow-up-right-circle-fill"></i>
              </div>
              <div>
                <div className="text-muted extra-small fw-bold text-uppercase">Total Payable</div>
                <h5 className="fw-bold mb-0 text-success">₹{parseFloat(stats?.totalPayable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h5>
                <small className="text-muted">To Suppliers</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pro Full Width Outstanding Summary Panel (Receivable & Payable) */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-header bg-white py-3">
          <div className="row g-3 align-items-center">
            {/* Title & Tabs */}
            <div className="col-12 col-md-6 d-flex align-items-center gap-3 flex-wrap">
              <h5 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                <i className="bi bi-journal-check text-primary"></i> Outstanding Balances Summary
              </h5>
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className={`btn px-3 ${activeTab === 'RECEIVABLE' ? 'btn-danger' : 'btn-outline-danger'}`}
                  onClick={() => setActiveTab('RECEIVABLE')}
                >
                  <i className="bi bi-person-lines-fill me-1"></i> Receivables (Customers) ({rawReceivables.length})
                </button>
                <button
                  type="button"
                  className={`btn px-3 ${activeTab === 'PAYABLE' ? 'btn-success' : 'btn-outline-success'}`}
                  onClick={() => setActiveTab('PAYABLE')}
                >
                  <i className="bi bi-building me-1"></i> Payables (Suppliers) ({rawPayables.length})
                </button>
              </div>
            </div>

            {/* Search Input & Export Button */}
            <div className="col-12 col-md-6 d-flex justify-content-md-end align-items-center gap-2">
              <div className="input-group input-group-sm" style={{ maxWidth: '300px' }}>
                <span className="input-group-text bg-light"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Filter by account name or mobile..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button className="btn btn-outline-secondary" type="button" onClick={() => setSearchTerm('')}>
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
              <Link to="/reports/statement" className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1">
                <i className="bi bi-file-earmark-text"></i> Statements
              </Link>
            </div>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '50px' }}>#</th>
                  <th>Account Name</th>
                  <th>Mobile Number</th>
                  <th className="text-end">Total Billed (₹)</th>
                  <th className="text-end">Paid Amount (₹)</th>
                  <th className="text-end">Remaining Outstanding (₹)</th>
                  <th className="text-center" style={{ width: '120px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {activeTab === 'RECEIVABLE' ? (
                  receivablesList.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-5 text-muted">
                        <i className="bi bi-check-circle-fill text-success fs-3 d-block mb-2"></i>
                        {searchTerm ? `No customer accounts matching "${searchTerm}".` : 'All customer accounts are settled! No outstanding receivables.'}
                      </td>
                    </tr>
                  ) : (
                    receivablesList.map((r, idx) => (
                      <tr key={r.Id}>
                        <td className="text-muted small">{idx + 1}</td>
                        <td>
                          <strong className="text-dark">{r.AccountName}</strong>
                        </td>
                        <td>{r.MobileNo ? <span className="small text-muted"><i className="bi bi-telephone me-1"></i>{r.MobileNo}</span> : '-'}</td>
                        <td className="text-end fw-semibold text-secondary">
                          ₹{parseFloat(r.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="text-end text-success fw-semibold">
                          ₹{parseFloat(r.paidAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="text-end fw-bold text-danger fs-6">
                          ₹{parseFloat(r.remainingAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="text-center">
                          <Link
                            to={`/reports/statement?accountId=${r.Id}`}
                            className="btn btn-sm btn-outline-info d-flex align-items-center justify-content-center gap-1 py-1"
                            title="View Full Ledger Statement"
                          >
                            <i className="bi bi-journal-text"></i> Statement
                          </Link>
                        </td>
                      </tr>
                    ))
                  )
                ) : payablesList.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      <i className="bi bi-check-circle-fill text-success fs-3 d-block mb-2"></i>
                      {searchTerm ? `No supplier accounts matching "${searchTerm}".` : 'All supplier accounts are settled! No outstanding payables.'}
                    </td>
                  </tr>
                ) : (
                  payablesList.map((p, idx) => (
                    <tr key={p.Id}>
                      <td className="text-muted small">{idx + 1}</td>
                      <td>
                        <strong className="text-dark">{p.AccountName}</strong>
                      </td>
                      <td>{p.MobileNo ? <span className="small text-muted"><i className="bi bi-telephone me-1"></i>{p.MobileNo}</span> : '-'}</td>
                      <td className="text-end fw-semibold text-secondary">
                        ₹{parseFloat(p.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-end text-primary fw-semibold">
                        ₹{parseFloat(p.paidAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-end fw-bold text-success fs-6">
                        ₹{parseFloat(p.remainingAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-center">
                        <Link
                          to={`/reports/statement?accountId=${p.Id}`}
                          className="btn btn-sm btn-outline-info d-flex align-items-center justify-content-center gap-1 py-1"
                          title="View Full Ledger Statement"
                        >
                          <i className="bi bi-journal-text"></i> Statement
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {activeTab === 'RECEIVABLE' && receivablesList.length > 0 && (
                <tfoot className="table-light fw-bold">
                  <tr>
                    <td colSpan="3" className="text-end text-muted">Total Receivables Summary:</td>
                    <td className="text-end text-secondary">
                      ₹{receivablesList.reduce((s, r) => s + r.totalAmount, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="text-end text-success">
                      ₹{receivablesList.reduce((s, r) => s + r.paidAmount, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="text-end text-danger fs-6">
                      ₹{receivablesList.reduce((s, r) => s + r.remainingAmount, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
              {activeTab === 'PAYABLE' && payablesList.length > 0 && (
                <tfoot className="table-light fw-bold">
                  <tr>
                    <td colSpan="3" className="text-end text-muted">Total Payables Summary:</td>
                    <td className="text-end text-secondary">
                      ₹{payablesList.reduce((s, p) => s + p.totalAmount, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="text-end text-primary">
                      ₹{payablesList.reduce((s, p) => s + p.paidAmount, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="text-end text-success fs-6">
                      ₹{payablesList.reduce((s, p) => s + p.remainingAmount, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
